import type { ReactNode } from 'react';
import { Check, Clock, MoreHorizontal, Pencil, Repeat, StickyNote, Trash2 } from 'lucide-react';
import { describeRepeat, isChecklistItemDone, type ChecklistItem } from '@peles/shared';
import { DropdownMenu } from '../common/DropdownMenu';
import { TaskConversationButton } from './TaskConversations';
import {
  DUE_TONE_CLASSES,
  TASK_STATUS_CLASSES,
  TASK_STATUS_LABELS,
  describeDue,
  initials,
} from './taskUi';

/** A task row's own tick — large enough to hit on a phone. */
export function TaskCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
        checked
          ? 'bg-green-600 border-green-600 text-white'
          : 'border-gray-300 hover:border-green-500 text-transparent hover:text-green-500'
      }`}
    >
      <Check size={14} strokeWidth={3} />
    </button>
  );
}

export function TaskDueBadge({ dueDate, todayKey }: { dueDate: Date | string; todayKey: string }) {
  const { label, tone } = describeDue(dueDate, todayKey);
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${DUE_TONE_CLASSES[tone]}`}
      data-tone={tone}
    >
      <Clock size={11} />
      {label}
    </span>
  );
}

export function TaskAssigneeChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
      <span
        aria-hidden
        className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold flex items-center justify-center"
      >
        {initials(name)}
      </span>
      {name}
    </span>
  );
}

export interface TaskRowProps {
  task: ChecklistItem;
  todayKey: string;
  onToggle: (task: ChecklistItem) => void;
  onOpen: (task: ChecklistItem) => void;
  onDelete?: (task: ChecklistItem) => void;
  /** Where the task lives ("Cohen Wedding · Vendors") — shown on views across lists. */
  context?: ReactNode;
  /** A template's offset in place of a date. */
  isTemplate?: boolean;
  selected?: boolean;
  onSelect?: (task: ChecklistItem, selected: boolean) => void;
  /** A drag handle, when the row can be reordered. */
  handle?: ReactNode;
}

function offsetLabel(days: number): string {
  if (days === 0) return 'Event day';
  return days > 0 ? `${days}d after event` : `${Math.abs(days)}d before event`;
}

export function TaskRow({
  task,
  todayKey,
  onToggle,
  onOpen,
  onDelete,
  context,
  isTemplate = false,
  selected,
  onSelect,
  handle,
}: TaskRowProps) {
  const done = isChecklistItemDone(task);
  const assigneeName = task.assignedToName || task.assignee;
  const showStatus = task.status === 'IN_PROGRESS' || task.status === 'BLOCKED';
  const repeat = describeRepeat(task.repeatUnit, task.repeatInterval);

  return (
    <div
      className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${
        selected ? 'bg-primary-50' : 'hover:bg-gray-50'
      }`}
      data-testid={`task-${task.id}`}
    >
      {handle}
      {onSelect && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={(e) => onSelect(task, e.target.checked)}
          aria-label={`Select ${task.title}`}
          className="mt-1.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      )}
      <TaskCheckbox
        checked={done}
        onChange={() => onToggle(task)}
        label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      />
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={() => onOpen(task)}
          className={`block w-full text-left text-sm leading-6 break-words focus:outline-none focus-visible:underline ${
            done ? 'line-through text-gray-400' : 'text-gray-900'
          }`}
        >
          {task.title}
        </button>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-0.5 empty:hidden">
          {isTemplate && task.dueDateOffsetDays != null && (
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
              <Clock size={11} />
              {offsetLabel(task.dueDateOffsetDays)}
            </span>
          )}
          {!isTemplate && task.dueDate && !done && (
            <TaskDueBadge dueDate={task.dueDate} todayKey={todayKey} />
          )}
          {showStatus && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded font-medium ${TASK_STATUS_CLASSES[task.status]}`}
            >
              {TASK_STATUS_LABELS[task.status]}
            </span>
          )}
          {repeat && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500" title={repeat}>
              <Repeat size={11} aria-hidden="true" />
              {repeat}
            </span>
          )}
          {assigneeName && <TaskAssigneeChip name={assigneeName} />}
          {task.notes && (
            <span className="inline-flex items-center text-gray-400" title={task.notes}>
              <StickyNote size={12} aria-label="Has notes" />
            </span>
          )}
          {done && task.completedByName && (
            <span className="text-xs text-gray-400">Done by {task.completedByName}</span>
          )}
          {context && <span className="text-xs text-gray-500 truncate">{context}</span>}
        </div>
      </div>
      {!isTemplate && (
        <span className="shrink-0 mt-0.5">
          <TaskConversationButton task={task} />
        </span>
      )}
      <DropdownMenu
        ariaLabel={`Actions for ${task.title}`}
        align="end"
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={`Actions for ${task.title}`}
            className="shrink-0 p-1.5 -mr-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <MoreHorizontal size={16} />
          </button>
        )}
        items={[
          { id: 'edit', label: 'Edit', icon: <Pencil size={14} />, onSelect: () => onOpen(task) },
          ...(onDelete
            ? [
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: <Trash2 size={14} />,
                  onSelect: () => onDelete(task),
                  dividerBefore: true,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}
